/** Your biggest day: the date lands with a thump, then that day's messages from you scroll past. */

import { gsap } from "gsap";

import { Avatar, Fit, Scene, Zone, useStageExtra, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import type { You } from "../../types";
import { count } from "../shared";

const BLOBS: BlobSpec[] = [{ colour: "yellow", x: 1060, y: 250, r: 160, seed: 9.1, wob: 0.22 }];

const MONTHS = ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"];
const WEEKDAYS = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];

// The feed's bottom fade. Rows rest just above it, so a short day sits at the
// bottom of the box like a chat, and nothing scrolls past an empty feed.
const FADE = 130;
// The feed's height on the designed stage (wrapped.css); a taller screen stretches it.
const FEED = 690;

export function BiggestDay({ day, name }: { day: NonNullable<You["biggest_day"]>; name: string }) {
  const [y, m, d] = day.date.split("-").map(Number);
  const weekday = WEEKDAYS[new Date(y, m - 1, d).getDay()];
  const lines = day.messages;
  // The times as the chat saw them: each line's own clock, not the viewer's.
  const times = lines.map((line) => line.at.slice(11, 16));
  const extra = useStageExtra();

  const root = useTimeline((s) => {
    s.rise("[data-part=kick]", 0.35, { y: 0 });
    s.slam(".d14", 0.45, { from: 4.2, dur: 0.34 });
    s.shake("[data-part=shake]", 0.79, 8, 26);
    s.slam(".dmar", 0.95);
    s.shake("[data-part=shake]", 1.25, 8, 20);
    s.rise("[data-part=weekday]", 1.4, { y: 20 });
    s.rise("[data-part=stat]", 1.55, { y: 24 });

    const col = s.q(".feed-col")[0];
    const pill = s.q(".fclock")[0];
    if (!col || !pill) return;
    s.tl.fromTo(".feed", { autoAlpha: 0, y: 120 }, { autoAlpha: 1, y: 0, duration: 0.5 }, 1.65);
    // The column starts just below the box and rises to its resting place.
    const height = col.offsetHeight;
    const bottoms = s.q(".fm").map((row) => row.offsetTop + row.offsetHeight);
    const start = FADE + height;
    pill.textContent = times[0];
    s.tl.fromTo(
      col,
      { y: start },
      {
        y: 0,
        duration: Math.min(3.3, Math.max(1.4, start / 1600)),
        ease: "power2.inOut",
        onUpdate() {
          // The pill shows the time of the newest row clear of the bottom fade.
          const shown = height - Number(gsap.getProperty(col, "y"));
          let i = 0;
          while (i + 1 < bottoms.length && bottoms[i + 1] <= shown) i++;
          pill.textContent = times[i];
        },
      },
      1.85,
    );
    s.sound("whoosh", 1.85);
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div data-part="kick" className="kick">
            Your biggest day
          </div>
        </Zone>
        {/* The date is the moment: centred in whatever height there is. */}
        <Zone at="middle">
          <div data-part="shake" className="layer">
            <div className="d14 disp">{d}</div>
            <Fit size={236} className="dmar disp" style={{ right: 60 }}>
              {MONTHS[m - 1]}
            </Fit>
            <div
              data-part="weekday"
              className="abs mono"
              // One digit is half as wide, so the weekday moves in beside it.
              style={{ left: d < 10 ? 350 : 560, top: 420, fontSize: 44, fontWeight: 700, letterSpacing: "0.08em" }}
            >
              {weekday}
            </div>
          </div>
          <div data-part="stat" className="line" style={{ top: 965 }}>
            You sent <b>{count(day.count)}</b> messages.{lines.length ? " Here they come." : ""}
          </div>
          {lines.length ? (
            // The feed moves down half the extra height with the date and grows by the other
            // half, so its foot follows the bottom edge and a taller screen shows more of the day.
            <div className="feed" style={{ height: FEED + extra / 2 }}>
              <div className="feed-col" style={{ top: "auto", bottom: FADE }}>
                {lines.map((line, i) => (
                  <div key={i} className="fm">
                    <Avatar name={name} inline />
                    <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                      <div>
                        <b style={{ color: "var(--wr-link)" }}>{name}</b>
                        <time>{times[i]}</time>
                      </div>
                      <p>{line.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="fade-t" />
              <div className="fade-b" />
              <div className="fclock">{times[times.length - 1]}</div>
            </div>
          ) : null}
        </Zone>
      </Scene>
    </div>
  );
}
